/*
 * Назначение: тонкий HTTP-клиент админки.
 * Функционал: единый fetch-обёрточник, который шлёт куки, парсит JSON,
 * нормализует ошибки и публикует событие "auth:unauthorized" на 401 —
 * чтобы AuthProvider/router сделал редирект на страницу логина.
 *
 * В dev (vite) запросы идут через прокси /api → http://localhost:8000.
 * В проде SPA и API на одном домене (https://ai19god.ru), кросс-запросов нет.
 */

export class ApiError extends Error {
  constructor(
    public status: number,
    public detail: string,
    public code?: string,
  ) {
    super(detail);
    this.name = "ApiError";
  }
}

export const AUTH_UNAUTHORIZED_EVENT = "auth:unauthorized";

type Json = unknown;

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: Json | undefined;
  headers?: Record<string, string>;
  /** Не публиковать событие auth:unauthorized при 401 (например, для самого /me). */
  silent401?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, headers = {}, silent401 = false } = options;

  const init: RequestInit = {
    method,
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const res = await fetch(path, init);

  if (res.status === 401) {
    if (!silent401) {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }
  }

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }

  if (!res.ok) {
    const detail =
      (data as { detail?: string } | null)?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, detail);
  }

  return data as T;
}

/** Загрузка файла (multipart/form-data). Не выставляем Content-Type — браузер
 * сам добавит boundary. Куки и обработка 401 — как в обычном request. */
async function requestForm<T>(
  path: string,
  formData: FormData,
  opts: { headers?: Record<string, string> } = {},
): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      ...(opts.headers ?? {}),
    },
    body: formData,
  });

  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
  }

  let data: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { detail: text };
    }
  }

  if (!res.ok) {
    const detail =
      (data as { detail?: string } | null)?.detail ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, detail);
  }

  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "GET" }),
  post: <T>(path: string, body?: Json, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "POST", body }),
  postForm: <T>(path: string, formData: FormData, opts?: { headers?: Record<string, string> }) =>
    requestForm<T>(path, formData, opts ?? {}),
  put: <T>(path: string, body?: Json, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "PUT", body }),
  patch: <T>(path: string, body?: Json, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};
